import {
  makeStyles,
  Typography as Mui4Typography,
  TypographyProps as Mui4TypographyPropsType,
  Theme,
} from '@material-ui/core'
import { default as Mui5Typography, TypographyProps as Mui5TypographyProps } from '@mui/material/Typography'
import { CSSProperties, forwardRef } from 'react'
import { useComponentVersion } from '../hooks'

export enum TypographyVersion {
  V1 = 1, // MUI4 + OS Theme
  V2 = 2, // MUI5 + Stratis
}

export type TypographyVariant =
  | 'body1'
  | 'body1SemiBold'
  | 'body1Bold'
  | 'body2'
  | 'body2Medium'
  | 'body3Medium'
  | 'body4'
  | 'caption1'
  | 'caption1Bold'
  | 'caption2'
  | 'h1Bold'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h4SemiBold'
  | 'h5'
  | 'h5SemiBold'
  | 'h5Bold'
  | 'h6'
  | 'h6SemiBold'
  | 'h6Bold'
  | 'subtitle1'
  | 'subtitle1Bold'
  | 'subtitle2'
  | 'helper1'
  | 'titleSemiBold'
  | 'modalTitle'
  | 'h4SemiBold2'

export interface TypographyProps extends Omit<Mui4TypographyPropsType, 'variant'> {
  colorHex?: string
  textVariant?: TypographyVariant
  variant?: Mui5TypographyProps['variant']
  sx?: Mui5TypographyProps['sx']
  component?: Mui5TypographyProps['component']
}

type StyleProps = {
  colorHex?: string
  textVariant?: TypographyVariant
}

export const getTypographyStyles = ({ colorHex = '#212121', textVariant = 'body1' }: StyleProps): CSSProperties => {
  switch (textVariant) {
    // body
    case 'body1':
      return {
        color: colorHex,
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '22px',
      }
    case 'body1SemiBold':
      return {
        color: colorHex,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '22px',
      }
    case 'body1Bold':
      return {
        color: colorHex,
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '22px',
      }
    case 'body2':
      return {
        color: colorHex,
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '22px',
      }
    case 'body2Medium':
      return {
        color: colorHex,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: 0,
      }
    case 'body3Medium':
      return {
        color: colorHex,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '18px',
      }
    case 'body4':
      return {
        color: colorHex,
        fontSize: '10px',
        fontWeight: 400,
        lineHeight: '16px',
      }

    // caption
    case 'caption1':
      return {
        color: colorHex,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '16px',
      }
    case 'caption1Bold':
      return {
        color: colorHex,
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '16px',
      }
    case 'caption2':
      return {
        color: colorHex,
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '16px',
      }

    // h
    case 'h1Bold':
      return {
        color: colorHex,
        fontSize: '48px',
        fontWeight: 600,
        lineHeight: 'normal',
      }

    case 'h2':
      return {
        color: colorHex,
        fontSize: '40px',
        fontWeight: 600,
        lineHeight: 'normal',
      }
    case 'h3':
      return {
        color: colorHex,
        fontSize: '34px',
        fontWeight: 400,
        lineHeight: 'normal',
      }
    case 'h4':
      return {
        fontSize: '24px',
        fontWeight: 400,
        lineHeight: 'normal',
      }
    case 'h4SemiBold':
      return {
        fontSize: '24px',
        fontWeight: 500,
        lineHeight: 'normal',
      }
    case 'h4SemiBold2':
      return {
        fontSize: '24px',
        fontWeight: 600,
        lineHeight: '32px',
      }
    case 'h5':
      return {
        color: colorHex,
        fontSize: '20px',
        fontWeight: 400,
        lineHeight: '24px',
      }
    case 'h5SemiBold':
      return {
        color: colorHex,
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '24px',
      }
    case 'h5Bold':
      return {
        color: colorHex,
        fontSize: '20px',
        fontWeight: 600,
        lineHeight: '24px',
      }
    case 'h6':
      return {
        color: colorHex,
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '24px',
      }
    case 'h6SemiBold':
      return {
        color: colorHex,
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '24px',
      }
    case 'h6Bold':
      return {
        color: colorHex,
        fontSize: '16px',
        fontWeight: 600,
        lineHeight: '24px',
      }

    // subtitles
    case 'subtitle1':
      return {
        color: colorHex,
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '20px',
      }
    case 'subtitle1Bold':
      return {
        color: colorHex,
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '20px',
      }
    case 'subtitle2':
      return {
        color: colorHex,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 'normal',
      }

    // helper
    case 'helper1':
      return {
        color: colorHex,
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '16px',
      }

    case 'titleSemiBold':
      return {
        fontSize: '24px',
        fontWeight: 600,
        color: colorHex,
      }

    case 'modalTitle':
      return {
        fontSize: '18px',
        fontWeight: 700,
        color: colorHex,
      }

    default:
      return {}
  }
}

const useMui4Styles = makeStyles<Theme, TypographyProps>(() => ({
  root: ({ colorHex, textVariant }) => ({
    ...getTypographyStyles({ colorHex, textVariant }),
  }),
}))

// * MUI Typography API Spec  https://mui.com/material-ui/api/Typography/
export const Typography = forwardRef<HTMLSpanElement, TypographyProps>((props, ref) => {
  const version = useComponentVersion('typography')
  const styleProps: StyleProps = {
    colorHex: props.colorHex,
    textVariant: props.textVariant,
  }
  const v4Styles = useMui4Styles(styleProps)

  const { colorHex, ...rest } = props

  //@ts-ignore
  //TODO: fix this up, something is wonky here
  if (colorHex) rest['color'] = colorHex

  switch (version) {
    case TypographyVersion.V2:
      return (
        <Mui5Typography ref={ref} sx={!props.variant ? { ...getTypographyStyles(styleProps) } : props.sx} {...rest}>
          {props.children}
        </Mui5Typography>
      )

    default:
      return (
        //@ts-ignore - ignore UX3 variant classes on UX2 component
        <Mui4Typography classes={{ root: v4Styles.root }} {...rest}>
          {props.children}
        </Mui4Typography>
      )
  }
})
